import { useState, useEffect } from "react";
import { useApp } from "./context/AppContext";
import { useUrlSync } from "./hooks/useUrlSync";
import { Layout } from "./components/Layout";
import { SidePanel } from "./components/SidePanel";
import { Dashboard } from "./views/Dashboard";
import { RequirementsList } from "./views/RequirementsList";
import { RequirementsTree } from "./views/RequirementsTree";
import { RequirementDetail } from "./views/RequirementDetail";
import { TestsList } from "./views/TestsList";
import { TestDetail } from "./views/TestDetail";
import { Process } from "./views/Process";
import { Settings } from "./views/Settings";
import { CreateRequirement } from "./views/CreateRequirement";
import { CreateTest } from "./views/CreateTest";
import { SourceManagement } from "./views/SourceManagement";
import { AuditLogReport } from "./views/AuditLogReport";
import { Login } from "./views/Login";

function MainContent() {
  const { currentView, treeViewActive } = useApp();

  switch (currentView) {
    case "dashboard":
      return <Dashboard />;
    case "requirements":
      return treeViewActive ? <RequirementsTree /> : <RequirementsList />;
    case "tests":
      return <TestsList />;
    case "process":
      return <Process />;
    case "settings":
      return <Settings />;
    case "create-requirement":
      return <CreateRequirement />;
    case "create-test":
      return <CreateTest />;
    case "source-management":
      return <SourceManagement />;
    case "audit-log":
      return <AuditLogReport />;
    default:
      return <RequirementsList />;
  }
}

function DetailPanel() {
  const { currentView, activeId, panelOpen } = useApp();

  if (!panelOpen || !activeId) return null;

  const isReqView = currentView === "requirements" || currentView === "dashboard";
  const title = activeId;

  return (
    <SidePanel title={title}>
      {isReqView ? <RequirementDetail /> : <TestDetail />}
    </SidePanel>
  );
}

export function App() {
  const [authUser, setAuthUser] = useState<{ id: string; name: string; role: string } | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [environment, setEnvironment] = useState("");

  // Apply saved theme on mount (ensures it persists across navigation, refresh, sign-out)
  useEffect(() => {
    try {
      const saved = localStorage.getItem("argus-theme");
      if (saved) document.documentElement.setAttribute("data-theme", saved);
    } catch { /* noop */ }
  }, []);

  useUrlSync();

  useEffect(() => {
    fetch("/me")
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data?.authenticated) {
          setAuthUser(data);
        }
      })
      .catch(() => {})
      .finally(() => setAuthChecked(true));

    fetch("/auth/config")
      .then(res => res.ok ? res.json() : null)
      .then(data => setEnvironment(data?.environment ?? ""))
      .catch(() => {});
  }, []);

  const handleLogout = async () => {
    await fetch("/logout", { method: "POST" });
    setAuthUser(null);
  };

  if (!authChecked) {
    return <div className="flex items-center justify-center h-screen bg-[var(--bg)]">
      <div className="text-sm text-[var(--text2)]">Loading...</div>
    </div>;
  }

  if (!authUser) {
    return <Login />;
  }

  return (
    <Layout authUser={authUser} environment={environment} onLogout={handleLogout}>
      <div className="flex h-full overflow-hidden w-full">
        <div className="flex-1 flex flex-col overflow-hidden min-h-0 min-w-0">
          <MainContent />
        </div>
        <DetailPanel />
      </div>
    </Layout>
  );
}
